import {Box, Chip, Stack, TextField} from "@mui/material";
import React, {ChangeEvent, KeyboardEvent, useState} from "react";
import {useLocalization} from "@smalox/shared";

interface EmailInputProps {
	emails: string[];
	setEmails?: (emails: string[]) => void;
	readonly?: boolean;
}

const EmailInput: React.FC<EmailInputProps> = ({emails, setEmails, readonly = false}) => {
	const {t} = useLocalization();
	const [isTyping, setIsTyping] = useState(false);

	const lastIndex = emails.length - 1;
	const inProgressValue = isTyping && emails[lastIndex] ? emails[lastIndex] : "";
	const trimmedProgressValue = inProgressValue.trim();
	const committed = isTyping ? emails.slice(0, -1) : emails;

	const isValidEmail = (email: string) => {
		const input = document.createElement("input");
		input.type = "email";
		input.value = email.trim();
		return input.checkValidity();
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (readonly) {
			return;
		}

		const targetValue = e.target.value;
		if (!isTyping && targetValue) {
			// start new pending
			setEmails?.(removeDuplicates([...emails, targetValue]));
			setIsTyping(true);
		}
		else if (targetValue) {
			// update pending
			setEmails?.(removeDuplicates([...committed, targetValue]));
		}
		else {
			// cleared out pending
			setEmails?.(committed);
			setIsTyping(false);
		}
	};

	const removeDuplicates = (list: string[]) => {
		return [...new Set(list)];
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (readonly) {
			return;
		}

		if (e.key === "Enter") {
			e.preventDefault();
			if (trimmedProgressValue && isValidEmail(trimmedProgressValue)) {
				setEmails?.([...committed, trimmedProgressValue]);
				setIsTyping(false);
			}
		}
	};

	const handleDelete = (emailToDelete: string) => {
		if (readonly) {
			return;
		}
		setEmails?.(committed.filter((e) => e !== emailToDelete));
	};

	return (
		<Stack spacing={1}>
			{!readonly && (
				<TextField
					label={t("bookings.shares.emails")}
					helperText={t("bookings.shares.emailsDescription")}
					type="email"
					value={inProgressValue}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					fullWidth
					inputProps={{"aria-label": "email-input"}}
				/>
			)}

			<Box display="flex" flexWrap="wrap" gap={1}>
				{committed.map((email) => (
					<Chip
						key={email}
						label={email}
						onDelete={readonly ? undefined : () => handleDelete(email)}
						variant="outlined"
						color="primary"
					/>
				))}

				{!readonly && trimmedProgressValue && !committed.includes(trimmedProgressValue) && (
					<Chip
						label={trimmedProgressValue}
						variant="outlined"
						color="default"
						sx={{opacity: 0.6, fontStyle: "italic"}}
					/>
				)}
			</Box>
		</Stack>
	);
};

export default EmailInput;
